import { useCallback, useMemo, useState } from "react";

import { isDefined } from "@/utils/types";

import { useRefValue } from "./useRefValue";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Values = Record<string, any>;
type InitialValues<T> = T | (() => T);
export type Errors<T> = Partial<Record<keyof T, string | undefined>>;
export type Validate<T> = Partial<{
    [K in keyof T]: (value: T[K], values: T) => string | undefined;
}>;

export function useFormFields<T extends Values>(
    initialValues: InitialValues<T>,
    validate?: Validate<T>
) {
    const savedValidate = useRefValue(validate);

    const [values, setValues] = useState(initialValues);
    const setValue = useCallback(<TField extends keyof T>(field: TField, value: T[TField]) => {
        setValues(values => ({ ...values, [field]: value }));
    }, []);

    const { isValid, fieldErrors } = useMemo(() => {
        const validation = savedValidate.current;
        const fieldErrors: Record<string, string | undefined> = {};

        if (!validation) return { isValid: true, fieldErrors: {} as Errors<T> };

        Object.keys(values).forEach(field => {
            if (Object.prototype.hasOwnProperty.call(validate, field)) {
                fieldErrors[field] = validation[field]!(values[field], values);
            } else {
                fieldErrors[field] = undefined;
            }
        });

        return {
            isValid: Object.values(fieldErrors).filter(isDefined).length === 0,
            fieldErrors: fieldErrors as Errors<T>,
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [values, savedValidate]);

    return {
        values,
        setValue,
        fieldErrors,
        isValid,
        reset: useCallback(() => setValues(initialValues), [initialValues]),
    };
}
